import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Modal from '@ui/modal/modal';
import { Dispatch, useEffect, useRef, useState } from 'react';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import { reactSelectAdminStyles } from '@ui/select/react-select/react-select-styles';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import { isValidEmail } from '../../../utils';
import toastTrigger from '@ui/toast/toast-trigger';
import CreatableReactSelect from '@ui/select/react-select/creatable-react-select';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EToastTypes } from '@ui/toast/toast.types';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';

type ConfirmUploadRegistrantsModalProps = {
  open: boolean;
  setOpen: Dispatch<React.SetStateAction<boolean>>;
  setTestEmailAddressesForSend?: Dispatch<React.SetStateAction<string[]>>;
  testEmailAddresses?: string[];
  sendEmails?: (isTestEmailSend: boolean) => void | Promise<void>;
  saveDisabled?: boolean;
  isSaving?: boolean;
  isSendingTest?: boolean;
  isBypassPasscodeListUploadForTestEmail?: boolean;
};

const ConfirmUploadRegistrantsModal = ({
  open,
  setOpen,
  sendEmails,
  saveDisabled,
  setTestEmailAddressesForSend,
  isSaving,
  isSendingTest,
  isBypassPasscodeListUploadForTestEmail,
}: ConfirmUploadRegistrantsModalProps) => {
  const [showAddTestEmails, setShowAddTestEmails] = useState(false); //test emails modal
  const [testEmailAddresses, setTestEmailAddresses] = useState<TReactSelectOption[]>([]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRef = useRef<any>(null);

  useEffect(() => {
    if (setTestEmailAddressesForSend) {
      setTestEmailAddressesForSend(testEmailAddresses.map((email) => email.label));
    }
  }, [testEmailAddresses, setTestEmailAddressesForSend]);

  const handleSendEmails = async () => {
    if (!sendEmails) return;
    // keep this modal open (showing the "Send Test" loading state) until the send finishes,
    // then close it. the finally guarantees we don't get stuck open if the send rejects.
    try {
      await sendEmails(true);
    } catch (e) {
      // downstream send handlers already surface errors via toast; just avoid an unhandled rejection
      console.error(e);
    } finally {
      setShowAddTestEmails(false);
    }
  };

  const sendTestEmailsDisabled = () => {
    return !testEmailAddresses.length || (isSendingTest ?? isSaving);
  };

  // disable the real "Send" while a save or a test send is in flight, to prevent concurrent requests
  const sendNowDisabled = () => saveDisabled || isSaving || isSendingTest;

  return (
    <>
      <Modal
        open={open}
        onRequestClose={isSaving || isSendingTest || showAddTestEmails ? undefined : () => setOpen(false)}
        backgroundColor="var(--gray-900)"
        customSize={{
          width: '640px',
          height: 'auto',
          maxHeight: '95vh',
          maxWidth: '95vw',
          minHeight: '250px',
        }}
        RenderHeaderContent={() => {
          return (
            <Container className="flex ai-center gap-8 H4 padding-v-24 padding-h-40" borderRadius="16px">
              Are you sure you want to send now?
            </Container>
          );
        }}
        RenderFooterContent={() => (
          <Container className="flex jc-space-between ai-center padding-h-40 padding-b-24" borderRadius="16px">
            <Container className="flex ai-center">
              <Button className="padding-0" onClick={() => setShowAddTestEmails(true)}>
                <Text className="flex ai-center gap-6" fontSize="14px">
                  <Icon name={ICONS.VIEWERS_EYE} size={20} /> Preview email
                </Text>
              </Button>
            </Container>
            <Container background="var(--gray-900)" className="flex jc-flex-end ai-flex-end gap-16" borderRadius="16px">
              <Button
                onClick={() => setOpen(false)}
                design="LocalBttnSec"
                className="L1 padding-v-16 padding-h-24"
                disabled={isSaving || isSendingTest}
              >
                Cancel
              </Button>
              <Button
                onClick={() => (sendEmails ? sendEmails(false) : undefined)}
                className="L1 padding-v-16 padding-h-24"
                disabled={sendNowDisabled()}
                disabledOpacity={0.6}
                loading={isSaving}
                backgroundColor={getThemeColorCSSVariable()}
                hoverBackgroundColor={getThemeColorsCssMixVariable()}
              >
                <Text
                  className="L1"
                  color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                >
                  Send
                </Text>
              </Button>
            </Container>
          </Container>
        )}
      >
        <Container className="padding-h-40">
          <Text className="P1">Be sure to check your registration confirmation email design prior to saving.</Text>
          <Text className="P1">Sending now will send the emails as is.</Text>
        </Container>
      </Modal>
      <Modal
        open={showAddTestEmails}
        customSize={{
          width: '500px',
          height: 'auto',
          maxHeight: '95vh',
          maxWidth: '95vw',
          minHeight: '250px',
        }}
        backgroundColor="var(--gray-900)"
        contentWrapperClassName="overflow-y-auto flex-1 flex-column"
        onRequestClose={isSendingTest || isSaving ? undefined : () => setShowAddTestEmails(false)}
        RenderHeaderContent={() => {
          return (
            <Container className="flex-column" borderRadius="16px" padding="24px 40px" gap="4px">
              <Text className="H4">Send test email</Text>
              {isBypassPasscodeListUploadForTestEmail && (
                <Text className="P1" design="GlobalIcoText2">
                  Note: email magic links will not work in test emails
                </Text>
              )}
            </Container>
          );
        }}
        RenderFooterContent={() => (
          <Container background="var(--gray-900)" className="flex jc-flex-end ai-flex-end gap-16" padding="24px 40px">
            <Button
              onClick={() => {
                setShowAddTestEmails(false);
                setTestEmailAddresses([]);
              }}
              design="LocalBttnSec"
              className="L1 padding-v-16 padding-h-24"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendEmails}
              className="padding-v-16 padding-h-24"
              disabled={sendTestEmailsDisabled()}
              disabledOpacity={0.6}
              loading={isSendingTest ?? isSaving}
              backgroundColor={getThemeColorCSSVariable()}
              hoverBackgroundColor={getThemeColorsCssMixVariable()}
            >
              <Text
                className="L1"
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              >
                Send Test
              </Text>
            </Button>
          </Container>
        )}
      >
        <Container className="flex-column padding-h-40 gap-16" background="var(--gray-900)" borderRadius="16px">
          <Text className="P1">Project must be published to reflect any email changes.</Text>
          <CreatableReactSelect
            disableClearWhenNotFocused
            placeholder="Type email and press enter"
            isMulti
            menuIsOpen={false}
            value={testEmailAddresses}
            ref={selectRef}
            styles={reactSelectAdminStyles({
              container: () => ({ width: '400px' }),
              control: () => ({ width: '400px' }),
              dropdownIndicator: () => ({ display: 'none' }),
            })}
            onChange={(newValue) => {
              setTestEmailAddresses(newValue as TReactSelectOption[]);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const target = e.target;
                if (target instanceof HTMLInputElement) {
                  e.preventDefault();

                  const value = target.value.trim();

                  if (!isValidEmail(value)) {
                    toastTrigger('Email entered is not a valid email address. Please try again.', {
                      type: EToastTypes.error,
                      autoClose: 5000,
                    });
                    return;
                  }

                  if (!testEmailAddresses.some((email) => email.label === value)) {
                    setTestEmailAddresses((prev) => [...prev, { label: value, value: value }]);

                    const select = selectRef.current;
                    if (select) {
                      select?.onInputChange('', 'set-value');
                    }
                  }
                }
              }
            }}
          />
        </Container>
      </Modal>
    </>
  );
};

export default ConfirmUploadRegistrantsModal;
